import React, { useState } from 'react';
import {
    Box,
    Grid,
    MenuItem,
    IconButton,
    Menu,
    Divider,
    ListItemIcon,
    FormControlLabel,
    Switch,
    Tooltip,
} from '@mui/material';
import {
    MoreVert as MoreIcon,
    FilterList as FilterListIcon,
} from '@mui/icons-material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import FilterDialog from '../components/filter/FilterDialog';
import FilterFields from '../components/filter/FilterFields';
import { SttButtonDelete, SttButtonNew, SttFormSelect } from './all';
import SttLoadingBar from './SttLoadingBar';
import { usePermission } from '../lib/usePermission';
import { PermissionRequirement } from '../lib/permissions';
import {
    FilterField,
    FilterOnChangeCallback,
} from '../components/filter/filterTypes';

type MainAction = {
    type?: 'create' | 'delete' | 'select' | 'component';
    caption?: string;
    onClick?: () => void;
    options?: any[];
    selected?: any;
    onChange?: (value: any) => void;
    component?: React.ReactNode;
    minWidth?: number | string;
    /** Permiso v8 que hace falta para ver la acción (p. ej. `<módulo>.create`). */
    permission?: PermissionRequirement;
};

type MenuItemType = {
    show?: boolean;
    caption?: string;
    onClick?: () => void;
    icon?: React.ReactNode;
    divider?: boolean;
};

type IconButtonType = {
    icon: React.ReactNode;
    onClick: () => void;
    caption?: string;
    tooltip?: string;
};

type FilterButton = {
    onClick: () => void;
};

type DownloadButton = {
    onClick: () => void;
};

type Props = {
    fields?: FilterField[];
    extraFields?: FilterField[];
    onChange?: FilterOnChangeCallback;
    onFilter?: () => void;
    skipSearchButton?: boolean;
    mainAction?: MainAction;
    menu?: MenuItemType[];
    menuIcon?: React.ReactNode;
    filterButton?: FilterButton;
    downloadButton?: DownloadButton;
    onToggleTrash?: (checked: boolean) => void;
    iconButtons?: IconButtonType[];
    loadingData?: boolean;
    [key: string]: any;
};

/**
 * SttTopControls
 * ==============
 *
 *     Componente para el control genérico en la cabecera de las páginas. Acepta campos para filtrado, opciones de filtro avanzado, etc.
 *
 * Props
 *
 *     * Function fields: campos para pintar (y filtrar). Ver tipos aceptados en /components/filter/FilterFields.js
 *     * Function extraFields: si se proporciona, se usarán para rellenar un modal con campos de filtro. El modal se muestra al pulsar el botón "avanzado" (setDialogOpen)
 *     * Function onChange({name, value}): callback general para cambios en cualquier campo. Cada campo puede proporcionar su propio onChange que tiene preferencia sobre este
 *     * Function onFilter: callback para el botón de búsqueda / aplicar cambios
 *
 *     * Object filterButton: show filter icon button
 *         - onClick: action
 *
 *     * Object downloadButton: show download button
 *         - onClick: action
 *
 *     * bool skipSearchButton
 *
 *     * Object mainAction: algunas pantallas pueden un botón por defecto que en general es el de añadir depósito, etc.
 *
 *         * onClick
 *         * component
 *         * caption
 *         * options
 *         * type: diferentes posibilidades dependiendo de mainAction.type
 *
 *          * create
 *              - onClick: callback para el botón
 *          * select
 *              - options: opciones para el select
 *              - selected: opción seleccionada por defecto en el select
 *              - onChange: callback para el select
 *          * delete
 *          * component: render component in mainAction.component
 *
 *         * permission: permiso v8 para ver la acción. Sin él, la acción no se pinta
 *           (la papelera no cambia: se queda con `.view`).
 *
 *     * array menu: muestra los "3 puntos" para abrir un menú con opciones. Cada compnente del menú tiene:
 *         - boolean show
 *         - string caption:
 *         - function onClick
 *         - Component icon
 *         - boolean divider: si está establecido pondrá un <Divider /> justo arriba del item.
 *
 *     * Component menuIcon: icono opcional para el menú desplegable ("3 puntos" por defecto).
 *     * Function onToggleTrash: callback for the trash switch. It will be called with the switch value.
 *         - This is best used alongside the useToggle hook defined in /src/lib/hooks.js
 *     * array iconButtons: muestra un botón en formato icono que realiza una función:
 *         - icon
 *         - onClick
 *         - caption
 *  * boolean loadingData: valor booleano para bloquear el filtrado hasta que se carguen los datos.
 */

export function SttTopControls({
    fields,
    extraFields,
    onChange,
    onFilter,
    _skipSearchButton,
    mainAction: mainActionProp,
    menu,
    menuIcon,
    filterButton,
    downloadButton,
    onToggleTrash,
    iconButtons,
    loadingData = false,
    ...rest
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const [buttonMenuDOMiObject, setButtonMenuDOMObject] =
        useState<HTMLElement | null>(null);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const { can } = usePermission();
    const mainAction =
        mainActionProp?.permission && !can(mainActionProp.permission)
            ? undefined
            : mainActionProp;

    // Setup relative sizes of the controls
    // TODO: remove filterButton and downloadButton when they're removed from Mship and Report
    const extra =
        extraFields ||
        filterButton ||
        downloadButton ||
        menu ||
        onToggleTrash ||
        iconButtons;
    let sizes: {
        fields: { xs: number; md?: number; lg?: number };
        extra?: { xs: number; md?: number; lg?: number };
    };
    if (fields && extra) {
        // Both fields and extra controls
        sizes = {
            fields: {
                xs: 12,
                md: 8,
                lg: 10,
            },
            extra: {
                xs: 12,
                md: 4,
                lg: 2,
            },
        };
    } else if (!fields && extra) {
        // Only extra controls (no fields)
        sizes = {
            fields: {
                xs: 12,
            },
            extra: {
                xs: 12,
            },
        };
    } else {
        sizes = {
            fields: {
                xs: 12,
            },
        };
    }

    /**
     * Render fields
     */
    function renderDefaultFields(sizes: {
        xs: number;
        md?: number;
        lg?: number;
    }) {
        if (fields) {
            let content: React.ReactElement;

            // If we only specify a single text field to filter by default, we turn it into a search form text field
            if (fields.length === 1 && fields[0]?.type === 'text') {
                const firstField = fields[0];
                content = (
                    <FilterFields
                        spacing={3}
                        onChange={onChange!}
                        fields={[
                            {
                                ...firstField,
                                type: 'search',
                                xs: firstField.xs ?? 12,
                                sm: firstField.sm ?? 12,
                                md: firstField.md ?? 12,
                                lg: firstField.lg ?? 10,
                                onEnterKey:
                                    'onEnterKey' in firstField
                                        ? firstField.onEnterKey
                                        : undefined,
                            } as FilterField,
                        ]}
                        action={renderMainAction()}
                    />
                );
            } else {
                content = (
                    <FilterFields
                        spacing={3}
                        onChange={onChange!}
                        fields={fields}
                        action={renderMainAction()}
                    />
                );
            }

            return <Grid size={sizes}>{content}</Grid>;
        }
    }

    /**
     * Render main action button
     */
    function renderMainAction(): React.ReactNode {
        let content: React.ReactNode;

        if (mainAction) {
            switch (mainAction.type) {
                case 'create':
                    content = (
                        <SttButtonNew
                            caption={mainAction.caption}
                            onClick={mainAction.onClick!}
                        />
                    );
                    break;
                case 'delete':
                    content = <SttButtonDelete onClick={mainAction.onClick} />;
                    break;
                case 'select':
                    content = (
                        <Box
                            sx={{
                                minWidth: mainAction.minWidth ?? 'auto',
                            }}
                        >
                            <SttFormSelect
                                options={mainAction.options}
                                onChange={({
                                    value,
                                }: {
                                    name: string | undefined;
                                    value: unknown;
                                }) => mainAction.onChange?.(value)}
                                defVal={mainAction.selected}
                            />
                        </Box>
                    );
                    break;
                case 'component':
                    content = mainAction.component;
                    break;
            }
        }
        return content;
    }

    function renderExtra(sizes: { xs: number; md?: number; lg?: number }) {
        // Extra components container should grow to take all available space (but should it really?)
        const mustGrow = true; // onToggleTrash && !(extraFields || filterButton || downloadButton || menu)

        return (
            <Grid size={sizes} style={mustGrow ? { flexGrow: 1 } : {}}>
                <Box display="flex" justifyContent="flex-end">
                    {/* --- Trash --- */}
                    {onToggleTrash && (
                        <FormControlLabel
                            id="trash-toggle"
                            control={
                                <Switch
                                    checked={
                                        undefined /* We don't care about this value because the switch has its own state and this will always be false first */
                                    }
                                    onChange={(ev: any) =>
                                        onToggleTrash(ev.target.checked)
                                    }
                                />
                            }
                            label={t('Trash')}
                        />
                    )}

                    {/* --- Popup filter --- */}
                    {extraFields && (
                        <IconButton
                            id="filter-button"
                            onClick={() => setDialogOpen(true)}
                        >
                            <FilterListIcon />
                        </IconButton>
                    )}

                    {filterButton /* Solo se usa en Report y Mship; TODO: Unificar esos 2 componentes y eliminar esto */ && (
                        <IconButton onClick={filterButton.onClick}>
                            <FilterListIcon />
                        </IconButton>
                    )}

                    {downloadButton /* Solo se usa en Report y Mship; TODO: Unificar esos 2 componentes y eliminar esto */ && (
                        <IconButton onClick={downloadButton.onClick}>
                            <CloudDownloadIcon />
                        </IconButton>
                    )}

                    <Box id="icon-buttons" display="flex" alignItems="center">
                        {iconButtons?.map((item: any, idx: any) => (
                            <React.Fragment key={idx}>
                                <Tooltip title={item.tooltip ?? ''} arrow>
                                    <IconButton
                                        id={`icon-button-${idx}`}
                                        onClick={item.onClick}
                                    >
                                        {item.icon}
                                    </IconButton>
                                </Tooltip>
                                {item.caption && (
                                    <Typography
                                        variant="caption"
                                        display="block"
                                        gutterBottom
                                    >
                                        {item.caption}
                                    </Typography>
                                )}
                            </React.Fragment>
                        ))}
                    </Box>

                    {menu && (
                        <>
                            <IconButton
                                onClick={(ev: any) =>
                                    setButtonMenuDOMObject(ev.currentTarget)
                                }
                            >
                                {menuIcon || <MoreIcon />}
                            </IconButton>
                            <Menu
                                anchorEl={buttonMenuDOMiObject}
                                open={buttonMenuDOMiObject !== null}
                                onClose={() => setButtonMenuDOMObject(null)}
                            >
                                {menu &&
                                    menu.map((item: any, idx: any) => (
                                        <div key={idx}>
                                            {item.divider && <Divider />}
                                            <MenuItem
                                                onClick={() => {
                                                    if (
                                                        item.onClick &&
                                                        item.onClick
                                                            .constructor ===
                                                            Function
                                                    ) {
                                                        item.onClick();
                                                    }
                                                    setButtonMenuDOMObject(
                                                        null,
                                                    );
                                                }}
                                            >
                                                {item.icon && (
                                                    <ListItemIcon>
                                                        {item.icon}
                                                    </ListItemIcon>
                                                )}
                                                {item.caption}
                                            </MenuItem>
                                        </div>
                                    ))}
                            </Menu>
                        </>
                    )}
                </Box>
            </Grid>
        );
    }

    // Apply filters, but only if there has been at least one change
    function filterClicked() {
        if (
            onFilter &&
            onFilter.constructor === Function /* && shouldOnFilterBeCalled */
        ) {
            onFilter();
            // setShouldOnFilterBeCalled(false);
        }
    }

    /**
     * Render menu
     */
    // -----| Render |-----
    return (
        <>
            <Box
                {...rest}
                sx={{ pointerEvents: loadingData ? 'none' : 'auto' }}
            >
                <Grid container spacing={3}>
                    {renderDefaultFields(sizes.fields)}

                    {mainAction && !fields && (
                        <Grid size={{ xs: 12, sm: 4, md: 3, lg: 2, xl: 1 }}>
                            {renderMainAction()}
                        </Grid>
                    )}

                    {extra && sizes.extra && renderExtra(sizes.extra)}
                </Grid>
            </Box>

            {extraFields && (
                <FilterDialog
                    title={t('Filter')}
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    fields={extraFields}
                    onChange={onChange!}
                    onApply={() => {
                        filterClicked();
                        setDialogOpen(false);
                    }}
                />
            )}
            {loadingData && <SttLoadingBar visible />}
        </>
    );
}
